// Accès Supabase Storage pour le bucket privé `documents`.
//
// Convention de chemin imposée par les policies Storage déjà sécurisées :
//   `<uuid-perimetre>/<nom-fichier>`
// où `<uuid-perimetre>` est `current_proprietaire_id()` (espace Propriétaire)
// ou `current_agence_id()` (espace Agence). Le premier dossier de l'objet DOIT
// correspondre au périmètre authentifié, sinon l'upload/lecture est refusé par RLS.

import { supabase } from "./supabase";

export const DOCUMENTS_BUCKET = "documents";

/** Nettoie un nom de fichier pour respecter la convention d'objet (1-63, minuscules). */
export function sanitizeFileName(name: string): string {
  const base = name
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return base.slice(-60) || "fichier";
}

/** Construit le chemin Storage `‹scopeId›/‹unique-nom›`. */
export function buildDocumentPath(scopeId: string, fileName: string): string {
  const unique = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  return `${scopeId}/${unique}-${sanitizeFileName(fileName)}`;
}

/** Convertit une `data:` URL (aperçu local) en Blob réel à uploader. */
export async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const response = await fetch(dataUrl);
  return response.blob();
}

/** Upload un Blob dans le bucket documents. Lève une erreur si l'upload échoue. */
export async function uploadDocumentBlob(path: string, blob: Blob): Promise<void> {
  const { error } = await supabase.storage.from(DOCUMENTS_BUCKET).upload(path, blob, {
    upsert: false,
    contentType: blob.type || "application/octet-stream",
  });
  if (error) throw error;
}

/** Supprime un objet du bucket documents (best effort : ne lève jamais). */
export async function removeDocumentObject(path: string): Promise<void> {
  if (!path) return;
  const { error } = await supabase.storage.from(DOCUMENTS_BUCKET).remove([path]);
  if (error) console.error("[Storage] suppression impossible", path, error.message);
}

/** Génère une URL signée (bucket privé) pour la consultation. */
export async function createSignedDocumentUrl(path: string, expiresIn = 3600): Promise<string> {
  if (!path) return "";
  const { data, error } = await supabase.storage.from(DOCUMENTS_BUCKET).createSignedUrl(path, expiresIn);
  if (error) {
    console.error("[Storage] URL signée impossible", path, error.message);
    return "";
  }
  return data?.signedUrl ?? "";
}